import React, { FunctionComponent, useMemo } from 'react';

import { useId } from '@radix-ui/react-id';
import { Controller, useForm } from 'react-hook-form';

import { RequestCodeRequest } from '@domains/login';

import { apiRequestCode } from '@api/methods';

import { sendRequestSmsEvent } from '@utils/analytics';
import { VALIDATION_MESSAGES, VALIDATORS } from '@utils/constants';
import { handleFormError } from '@utils/form';
import { PhoneStep, Step, StepKind } from '@utils/login-dialog';
import { makeInfoPageRoute } from '@utils/route';
import { SnackBar } from '@utils/snackbar';
import { applyTypograf } from '@utils/typograf';

import { Button } from '@components/common/Button/Button';
import { InvisibleCaptcha } from '@components/common/InvisibleCaptcha/InvisibleCaptcha';
import { useInvisibleCaptcha } from '@components/common/InvisibleCaptcha/use-invisible-captcha';
import { Link } from '@components/common/Link/Link';
import { StyledCheckbox } from '@components/common/StyledCheckbox/StyledCheckbox';
import { RuPhoneInput } from '@components/common/StyledInput';

import styles from './LoginDialogPhoneForm.module.scss';

const dataProcessingPolicyText1 = applyTypograf(
    'Я согласен на обработку персональных данных\nв соответствии с',
);
const dataProcessingPolicyText2 = applyTypograf(
    'Политикой в отношении обработки\nперсональных данных',
);
const userAgreementText1 = applyTypograf('Я принимаю условия');
const userAgreementText2 = applyTypograf('Пользовательского соглашения');

type FormValues = {
    phone: string;
    hasAcceptedDataProcessingPolicy: boolean;
    hasAcceptedUserAgreement: boolean;
};

export type LoginDialogPhoneFormProps = Readonly<{
    step: PhoneStep;
    setStep: (step: Step) => void;
}>;

export const LoginDialogPhoneForm: FunctionComponent<LoginDialogPhoneFormProps> = (props) => {
    const { step, setStep } = props;

    const dataProcessingPolicyCheckboxId = useId();
    const userAgreementCheckboxId = useId();

    const invisibleCaptcha = useInvisibleCaptcha();
    const { getCaptchaToken, isCaptchaLoading } = invisibleCaptcha;

    const initialValues = useMemo<FormValues>(
        () => ({
            phone: step.phone,
            hasAcceptedDataProcessingPolicy: step.hasAcceptedDataProcessingPolicy,
            hasAcceptedUserAgreement: step.hasAcceptedUserAgreement,
        }),
        [step.hasAcceptedDataProcessingPolicy, step.hasAcceptedUserAgreement, step.phone],
    );

    const form = useForm<FormValues>({ defaultValues: initialValues, shouldUnregister: true });

    const handleSubmit = async (values: FormValues): Promise<void> => {
        let captchaToken: string | undefined;
        try {
            captchaToken = await getCaptchaToken();
        } catch {
            return;
        }

        const payload: RequestCodeRequest = {
            phone: values.phone,
            captcha_token: captchaToken,
        };

        try {
            const response = await apiRequestCode(payload);
            sendRequestSmsEvent();
            setStep({
                kind: StepKind.Code,
                phone: values.phone,
                hasAcceptedDataProcessingPolicy: values.hasAcceptedDataProcessingPolicy,
                hasAcceptedUserAgreement: values.hasAcceptedUserAgreement,
                lifetime: response.lifetime,
                resendDelay: response.resend_delay,
            });
        } catch (error) {
            SnackBar.handleApiError(error);
            handleFormError(form, error);
        }
    };

    const handleLegalLoginButtonClick = (): void => {
        setStep({ kind: StepKind.Legal });
    };

    return (
        <>
            <div className={styles.root}>
                <form onSubmit={form.handleSubmit(handleSubmit)} className={styles.form}>
                    <Controller
                        control={form.control}
                        name="phone"
                        rules={{
                            required: VALIDATION_MESSAGES.required,
                            validate: VALIDATORS.phoneValidator,
                        }}
                        render={({ field, fieldState }) => (
                            <RuPhoneInput
                                label="Телефон*"
                                hintMessage={fieldState.error?.message}
                                hasError={fieldState.error?.message !== undefined}
                                isKeepHintPlace
                                disabled={form.formState.isSubmitting}
                                className={styles.phoneInput}
                                {...field}
                            />
                        )}
                    />
                    <Controller
                        control={form.control}
                        name="hasAcceptedDataProcessingPolicy"
                        rules={{
                            required: VALIDATION_MESSAGES.required,
                            validate: VALIDATORS.isChecked,
                        }}
                        render={({ field, fieldState, formState }) => (
                            <StyledCheckbox
                                id={dataProcessingPolicyCheckboxId}
                                className={styles.dataProcessingPolicyCheckbox}
                                hasError={fieldState.error !== undefined}
                                isDisabled={formState.isSubmitting}
                                {...field}
                            />
                        )}
                    />
                    <label
                        htmlFor={dataProcessingPolicyCheckboxId}
                        className={styles.dataProcessingPolicyCheckboxLabel}
                    >
                        {dataProcessingPolicyText1}{' '}
                        <Link
                            route={makeInfoPageRoute('privacy')}
                            target="_blank"
                            className={styles.labelLink}
                        >
                            {dataProcessingPolicyText2}
                        </Link>
                    </label>
                    <Controller
                        control={form.control}
                        name="hasAcceptedUserAgreement"
                        rules={{
                            required: VALIDATION_MESSAGES.required,
                            validate: VALIDATORS.isChecked,
                        }}
                        render={({ field, fieldState, formState }) => (
                            <StyledCheckbox
                                id={userAgreementCheckboxId}
                                className={styles.userAgreementCheckbox}
                                hasError={fieldState.error !== undefined}
                                isDisabled={formState.isSubmitting}
                                {...field}
                            />
                        )}
                    />
                    <label
                        htmlFor={userAgreementCheckboxId}
                        className={styles.userAgreementCheckboxLabel}
                    >
                        {userAgreementText1}{' '}
                        <Link
                            route={makeInfoPageRoute('eula')}
                            target="_blank"
                            className={styles.labelLink}
                        >
                            {userAgreementText2}
                        </Link>
                    </label>

                    <Button
                        isLoading={form.formState.isSubmitting || isCaptchaLoading}
                        type="submit"
                        variant="green"
                        isFullWith
                        className={styles.button}
                        data-testid="login-request-code-btn"
                    >
                        Получить смс-код
                    </Button>
                </form>

                <div className={styles.split}>или</div>

                <Button
                    type="button"
                    variant="white"
                    isFullWith
                    onClick={handleLegalLoginButtonClick}
                    disabled={form.formState.isSubmitting || isCaptchaLoading}
                >
                    {applyTypograf('Войти как юр. лицо')}
                </Button>
            </div>
            <InvisibleCaptcha {...invisibleCaptcha} />
        </>
    );
};
